import {
  CSSProperties,
  Dispatch,
  HTMLProps,
  JSX,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { ActionMeta, components, FilterOptionOption, InputActionMeta } from 'react-select';

import Container from '@ui/container/container';
import CreatableReactSelect from '@ui/select/react-select/creatable-react-select';
import CustomReactSelect from '@ui/select/react-select/custom-react-select';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import useClickAwayListener from '@/utils/use-click-away-listener';
import useOnFocusAny from '@/utils/use-on-focus-any';
import { assertIsCustomReactOption } from '@/utils/assertions/assert-is-custom-react-option';
import { assertIsArray } from '@/utils/assertions/assert-is-array';
import { ReactSelectInstance, ReactSelectInstanceRefObject, TReactSelectOption } from '../custom-react-select.types';
import { mergeRefs } from '@/utils/merge-refs';
import Option from './components/option';
import MultiValue from './components/multi-value';
import getTruncatedCreatableMultiReactSelectStyles from './helpers/get-truncated-creatable-multi-react-select-styles';
import useScrollToFocusedRemoveItem from './helpers/use-scroll-to-focused-remove-item';
import useCalculateViewableItems from './helpers/use-calculate-viewable-items';
import handleScrollAfterSelect from './helpers/handle-scroll-after-select';
import useGetMenuPlacement from './helpers/use-get-menu-placement';
import TruncatedCreatableMultiReactSelectHeader from './components/truncated-creatable-multi-react-select-header';
import useBlurEverythingOnTabAwayFromInput from './helpers/use-blur-everything-on-tab-away-from-input';
import useAsControlledComponent from './helpers/use-as-controlled-component';

import './truncated-creatable-multi-react-select.css';
import { ContainerProps } from '@ui/container/container.types';
import { TextProps } from '@ui/text/text/text.types';

type TTruncatedCreatableMultiReactSelectProps = {
  defaultValue?: TReactSelectOption[];
  /** only pass in value if you want the component to be controlled */
  value?: TReactSelectOption[];
  options?: TReactSelectOption[];
  onMenuClose?: (selectedOptions: TReactSelectOption[]) => void;
  disabled?: boolean;
  viewableItems?: number;
  controlWidth?: CSSProperties['width'];
  label?: string;
  placeholder?: ReactNode;
  onChange?: (selectedOptions: TReactSelectOption[], actionMeta: ActionMeta<unknown>) => void;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  showHeader?: boolean;
  onFocusChange?: (isFocused: boolean) => void;
  hideUnfocusedMenu?: boolean;
  dynamicMenuPlacement?: boolean;
  dynamicMenuPlacementOffset?: number;
  menuListMaxHeight?: CSSProperties['maxHeight'];
  Components?: {
    UnfocusedCustomSelectedOptionComponent?: React.FC<{
      option: TReactSelectOption;
    }>;
  } & Partial<typeof components>;
  styles?: ReturnType<typeof reactSelectAdminStyles>;
  id?: string;
  className?: string;
  ref?: ReactSelectInstanceRefObject | ReactSelectInstanceRefObject[];
  autoCalculateViewableItems?: boolean;
  isCreatable?: boolean;
  isFocusedWithin?: boolean;
  setIsFocusedWithin?: Dispatch<SetStateAction<boolean>>;
  isLoading?: boolean;
  loadingMessage?: () => JSX.Element;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  cannotClearOptions?: TReactSelectOption[];
  onCannotClearOptions?: (options: TReactSelectOption[]) => void;
  isMulti?: boolean;
  recalculateViewableItems?: number;
  noBorder?: boolean;
  openMenuOnFocus?: boolean;
  noOptionsMessage?: string;
  filterOptions?: ((option: FilterOptionOption<unknown>, inputValue: string) => boolean) | null;
  unfocusedMultiValueContainerProps?: Partial<ContainerProps>;
  unfocusedMultiValueImageProps?: HTMLProps<HTMLImageElement>;
  unfocusedMultiValueTextProps?: Partial<TextProps>;
  unfocusedMultiValuePlusMoreContainerProps?: Partial<ContainerProps>;
  focusedMultiValueContainerProps?: Partial<ContainerProps>;
  virtualized?: boolean;
};

const TruncatedCreatableMultiReactSelect = memo(
  ({
    virtualized,
    viewableItems: viewableItemsProp,
    disabled,
    defaultValue,
    value,
    options: propsOptions,
    onMenuClose,
    controlWidth,
    label = '',
    placeholder = '',
    onChange,
    showHeader = true,
    onFocusChange,
    hideUnfocusedMenu,
    dynamicMenuPlacement = false,
    dynamicMenuPlacementOffset = 0,
    menuListMaxHeight,
    Components,
    styles,
    id,
    className,
    ref,
    autoCalculateViewableItems,
    isCreatable = true,
    isFocusedWithin,
    setIsFocusedWithin,
    onInputChange,
    isLoading,
    loadingMessage,
    onKeyDown,
    cannotClearOptions,
    onCannotClearOptions,
    isMulti = true,
    recalculateViewableItems,
    noBorder,
    openMenuOnFocus = true,
    noOptionsMessage = 'No options found',
    filterOptions,
    unfocusedMultiValueContainerProps,
    unfocusedMultiValueImageProps,
    unfocusedMultiValueTextProps,
    unfocusedMultiValuePlusMoreContainerProps,
    focusedMultiValueContainerProps,
  }: TTruncatedCreatableMultiReactSelectProps) => {
    const SelectWrapper = isCreatable ? CreatableReactSelect : CustomReactSelect;

    const containerRef = useRef<HTMLDivElement>(null);
    const selectRef = useRef<ReactSelectInstance>(null);
    const moreItemsRef = useRef<HTMLDivElement | null>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<TReactSelectOption[]>(defaultValue || value || []);
    const [tabbedIn, setTabbedIn] = useState(false);

    useAsControlledComponent({
      value,
      selectedOptions,
      setSelectedOptions,
      selectRef,
    });

    const { menuPlacement } = useGetMenuPlacement({
      isOpen,
      selectRef,
      containerRef,
      dynamicMenuPlacement,
      dynamicMenuPlacementOffset,
    });

    const { viewableItems, setRecalculateCounter } = useCalculateViewableItems({
      autoCalculateViewableItems,
      viewableItemsProp,
      selectRef,
      containerRef,
      moreItemsRef,
      selectedOptions,
      tabbedIn,
      isControlledComponent: !!value,
    });

    useEffect(() => {
      if (recalculateViewableItems) {
        setRecalculateCounter((prev) => prev + 1);
      }
    }, [recalculateViewableItems, setRecalculateCounter]);

    useScrollToFocusedRemoveItem({ tabbedIn, selectRef });

    useBlurEverythingOnTabAwayFromInput({ selectRef, setTabbedIn, setIsFocusedWithin });

    useEffect(() => {
      if (disabled) return;
      onFocusChange?.(tabbedIn);
    }, [onFocusChange, tabbedIn, disabled]);

    const setTabbedInHelper = useCallback(
      (isTabbedIn: boolean) => {
        if (disabled) return;
        setTabbedIn(isTabbedIn);
      },
      [disabled]
    );

    useClickAwayListener({
      ref: containerRef,
      open: true,
      resetFocusedOnEscape: false,
      onTabAway: () => setTabbedInHelper(false),
      onPointerDownInside: () => setTabbedInHelper(true),
      onPointerUpInside: () => setTabbedInHelper(true),
      onPointerDownOutside: () => setTabbedInHelper(false),
      onPointerUpOutside: () => setTabbedInHelper(false),
    });

    useOnFocusAny({
      ref: containerRef,
      onFocus: () => setTabbedInHelper(true),
      onBlur: () => setTabbedInHelper(false),
    });

    const handleMenuClose = useCallback(() => {
      setIsOpen(false);
      if (disabled) return;
      onMenuClose?.(selectedOptions);
      setTimeout(() => {
        setRecalculateCounter((prev) => prev + 1);
      }, 0);
    }, [disabled, onMenuClose, selectedOptions, setRecalculateCounter]);

    const cannotClearOptionsMap = useMemo(() => {
      return new Map((cannotClearOptions || []).map((o) => [o.value, o]));
    }, [cannotClearOptions]);

    const handleChange = useCallback(
      (newValue: unknown, actionMeta: ActionMeta<unknown>) => {
        if (disabled) return;

        let newValueAsArrray = Array.isArray(newValue) ? newValue : [newValue];

        // if not multi-select, we only want the selected option
        if (!isMulti && actionMeta.action === 'select-option' && actionMeta.option) {
          newValueAsArrray = [actionMeta.option];
        }

        try {
          assertIsArray<TReactSelectOption>(newValueAsArrray);
        } catch (error) {
          console.error('Error asserting values TruncatedCreatableMultiReactSelect:', error);
          return;
        }

        const newValuesMap = new Map(newValueAsArrray.map((o) => [o.value, o]));

        if (isMulti) {
          // if newValuesArray is missing one of the cannotClearOptions
          // then call the onCannotClearCodes callback and return before updating
          const missingRequiredValues = cannotClearOptions?.filter((cco) => !newValuesMap.has(cco.value));
          if (missingRequiredValues?.length) {
            return onCannotClearOptions?.(missingRequiredValues);
          }
        }

        // if we have a value prop then we are in a controlled component
        // so only set selected options if uncontrolled
        if (!value) {
          setSelectedOptions(newValueAsArrray);
        }
        onChange?.(newValueAsArrray, actionMeta);

        if (actionMeta.action === 'select-option') {
          handleScrollAfterSelect({ selectRef });
        }
      },
      [disabled, isMulti, value, onChange, cannotClearOptions, onCannotClearOptions]
    );

    const getNoOptionsMessage = useCallback(() => {
      if (isCreatable) {
        return <div className="C2 text-left">Type to create new tag</div>;
      }
      return <div className="C2 text-left">{noOptionsMessage}</div>;
    }, [isCreatable, noOptionsMessage]);

    const refs = useMemo(() => {
      if (!ref) return [];
      if (Array.isArray(ref)) return ref;
      return [ref];
    }, [ref]);

    return (
      <Container
        maxWidth="100%"
        className={classNames('truncated-creatable-multi-react-select-wrapper', {
          isFocused: !disabled && tabbedIn,
          disabled,
          ['truncated-creatable-multi-react-select__control-no-border']: noBorder,
        })}
        ref={containerRef}
        width="100%"
        data-component="TruncatedCreatableMultiReactSelect"
      >
        <TruncatedCreatableMultiReactSelectHeader
          selectRef={selectRef}
          showHeader={showHeader}
          label={label}
          disabled={disabled}
        />

        <Container className="flex fd-row jc-flex-start ai-center radius-8 truncated-creatable-multi-react-select-container">
          <SelectWrapper
            id={id}
            virtualized={virtualized}
            menuPlacement={menuPlacement}
            wrapperClass={`width-100-percent ${className}`}
            clearAllClass="truncated-creatable-multi-react-select__clear-all"
            disableClearWhenNotFocused
            /* according to figma, there is no clear all indicator, but keeping clearAllClass above in case. */
            isClearable={false}
            placeholder={placeholder}
            createOptionPosition="first"
            noOptionsMessage={() => getNoOptionsMessage()}
            filterOption={filterOptions}
            openMenuOnFocus={openMenuOnFocus}
            /* do not set isMulti to false or both data and UI becom inconsistent, we will handle single via business logic */
            isMulti
            ref={mergeRefs(selectRef, ...refs)}
            isDisabled={disabled || isLoading}
            isLoading={isLoading}
            loadingMessage={loadingMessage}
            classNamePrefix="truncated-creatable-multi-react-select"
            options={propsOptions}
            hideSelectedOptions={false}
            onFocus={() => {
              handleScrollAfterSelect({ selectRef });
              setTabbedInHelper(true);
            }}
            value={selectedOptions}
            defaultValue={defaultValue}
            tabSelectsValue={false}
            onInputChange={onInputChange}
            onKeyDown={(e) => {
              onKeyDown?.(e);
              if (e.defaultPrevented) return;
              // preventDefault on spacebar because spacebar selects values in react-select
              // however, we then need to manually add a space to the input value
              if (e.key === ' ' && isCreatable) {
                e.preventDefault();
                const select = selectRef.current;
                if (select) {
                  select?.onInputChange((select?.props?.inputValue || '') + ' ', {
                    action: 'set-value',
                    prevInputValue: select?.props?.inputValue || '',
                  });
                }
              }
            }}
            closeMenuOnSelect={false}
            onMenuOpen={() => setIsOpen(true)}
            onMenuClose={handleMenuClose}
            onChange={handleChange}
            components={{
              ...Components,
              MultiValueRemove:
                Components?.MultiValueRemove ||
                ((option) => {
                  const data = option.data;
                  try {
                    assertIsCustomReactOption(data);
                  } catch (error) {
                    console.error(error);
                    return null;
                  }
                  const cannotRemove = cannotClearOptionsMap.has(data.value);
                  if (cannotRemove) return null;
                  return <components.MultiValueRemove {...option} />;
                }),
              /* Cannot get this working properly, but would really like it to */
              // MenuList: (props) => {
              //   const { options, children, maxHeight } = props;
              //   return (
              //     <components.MenuList
              //       {...props}
              //       innerProps={{
              //         ...props.innerProps,
              //         style: {
              //           ...props.innerProps.style,
              //           height: maxHeight || 108,
              //         },
              //       }}
              //     >
              //       <Virtuoso
              //         data={options}
              //         height={maxHeight || 108}
              //         initialItemCount={propsOptions?.length}
              //         initialScrollTop={0}
              //         itemContent={(idx) => {
              //           if (Array.isArray(children)) {
              //             const child = children[idx];
              //             if (idx === 0) {
              //               console.log(child);
              //             }
              //             const optionProps = child.props;
              //             if (Components?.Option) {
              //               return <Components.Option {...optionProps} />;
              //             }
              //             return <Option {...optionProps} data={optionProps?.data} />;
              //           }
              //           return <></>;
              //         }}
              //       />
              //     </components.MenuList>
              //   );
              // },
              Option:
                Components?.Option ||
                ((option) => {
                  const data = option.data;
                  try {
                    assertIsCustomReactOption(data);
                  } catch (error) {
                    console.error(error);
                    return null;
                  }
                  return <Option {...option} data={data} />;
                }),
              MultiValue:
                Components?.MultiValue ||
                ((props) => {
                  const option = props.data;
                  const values = props.selectProps.value;
                  try {
                    assertIsCustomReactOption(option);
                    assertIsArray<TReactSelectOption>(values);
                  } catch (e) {
                    console.error('Error asserting values TruncatedCreatableMultiReactSelect:', e);
                    return null;
                  }

                  return (
                    <MultiValue
                      props={props}
                      option={option}
                      values={values}
                      selectRef={selectRef}
                      containerRef={containerRef}
                      moreItemsRef={moreItemsRef}
                      hideUnfocusedMenu={!!hideUnfocusedMenu}
                      tabbedIn={tabbedIn}
                      viewableItems={viewableItems}
                      cannotClearOptionsMap={cannotClearOptionsMap}
                      unfocusedContainerProps={unfocusedMultiValueContainerProps}
                      unfocusedImageProps={unfocusedMultiValueImageProps}
                      unfocusedTextProps={unfocusedMultiValueTextProps}
                      unfocusedPlusMoreContainerProps={unfocusedMultiValuePlusMoreContainerProps}
                      focusedMultiValueContainerProps={focusedMultiValueContainerProps}
                    />
                  );
                }),
            }}
            classNames={{
              placeholder: () => 'S3',
              input: () => 'S3',
              menu: () => 'overflow-hidden',
            }}
            styles={getTruncatedCreatableMultiReactSelectStyles({
              controlWidth,
              menuListMaxHeight,
              hasSelectedOptions: !!selectedOptions.length,
              isFocusedWithin: !!isFocusedWithin,
              styles,
            })}
          />
        </Container>
      </Container>
    );
  }
);

export default TruncatedCreatableMultiReactSelect;
